import { useWorkActions, WorkActionsProvider } from '@/components/work/WorkActionsContext';
import type { WorkDocumentOutput } from '@/lib/work-document-output';
import { type CSSProperties, useId, useMemo, useState } from 'react';
import type { DashboardComposition } from '@shared/types/dashboard-composition';
import type { ToolPageMode } from '@shared/types/prebuilt-tools';
import type { AnyViewEnvelope } from '@shared/types/views';
import { ViewRouter } from '@/components/views/ViewRouter';
import { ToolSectionNav } from '@/components/tools/ToolSectionNav';
import { isRedundantVisibleLabel } from '@/lib/ui-editorial-sweeper';

export type ToolSectionId = 'artifact' | 'summary' | 'why' | 'actions' | 'context' | 'detail';

interface ToolResultPageRendererProps {
  composition: DashboardComposition;
  viewEnvelopesByInstanceId: Record<string, AnyViewEnvelope | null>;
  /** Legacy callers retain these hints; persisted placement is authoritative. */
  pageMode: ToolPageMode;
  sectionOrder?: ToolSectionId[];
  pageTitle?: string;
  documentOutputs?: Record<string, WorkDocumentOutput>;
  requestedOutput?: WorkDocumentOutput | null;
}

/** One renderer for preview, saved runs and read models. Never groups or invents content. */
export const ToolResultPageRenderer = ({ composition, viewEnvelopesByInstanceId, pageTitle, documentOutputs, requestedOutput }: ToolResultPageRendererProps) => {
  const anchorPrefix = useId();
  const workSurface = useWorkActions();
  const views = useMemo(() => [...composition.views].filter(view => {
    const payload = viewEnvelopesByInstanceId[view.id];
    if (!workSurface?.actionsElsewhere || payload?.view_type !== 'action_list') return true;
    return payload.data.items.some(item => !workSurface.actions.some(work => work.context.sourceActionId === item.id && work.context.sourceRunId === workSurface.sourceRunId));
  }).sort((a, b) =>
    a.placement.position.row - b.placement.position.row || a.placement.position.col - b.placement.position.col,
  ), [composition,viewEnvelopesByInstanceId,workSurface]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const anchor = (id: string) => `${anchorPrefix}-${id}`;
  const labelFor = (id: string) => {
    const view = views.find((candidate) => candidate.id === id);
    const payload = viewEnvelopesByInstanceId[id];
    return view?.titleOverride || view?.viewDefinition.displayMeta.title
      || (payload && 'title' in payload.data ? payload.data.title : null) || 'Résultat';
  };

  if (!views.length) return <p className="p-5 text-sm text-domino-inkSoft">Aucun résultat disponible.</p>;
  if (requestedOutput === null) return <p role="alert" className="p-5 text-sm text-red-700">La référence du document demandé est invalide.</p>;
  if (requestedOutput && !Object.values(documentOutputs ?? {}).some(output =>
    output.tabId === requestedOutput.tabId && output.sectionId === requestedOutput.sectionId)) {
    return <p role="alert" className="p-5 text-sm text-red-700">Le document demandé est introuvable dans ce résultat.</p>;
  }

  return (
    <div className="space-y-5">
      {views.length > 3 && <details className="mx-4 text-sm text-domino-inkSoft sm:mx-5 lg:mx-6"><summary className="cursor-pointer py-2">Sommaire</summary><ToolSectionNav
        items={views.map((view) => ({ id: view.id, label: labelFor(view.id) }))}
        activeId={views.some((view) => view.id === activeId) ? activeId : views[0]?.id ?? null}
        onSelect={(id) => {
          setActiveId(id);
          document.getElementById(anchor(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      /></details>}
      <div className="grid grid-cols-1 gap-5 px-4 pb-5 sm:px-5 lg:grid-cols-12 lg:px-6">
        {views.map((view) => {
          const payload = viewEnvelopesByInstanceId[view.id] ?? null;
          const label = labelFor(view.id);
          const span = Math.max(1, Math.min(12, Math.round(view.placement.position.colSpan / Math.max(1, composition.layout.columns) * 12)));
          const content = <ViewRouter viewDefinition={view.viewDefinition} payload={payload}
            titleOverride={label} hideTitle={Boolean(pageTitle && isRedundantVisibleLabel(pageTitle, label))}
            fallbackReason="dashboard_view_payload_missing" />;
          return (
            <section key={view.id} id={anchor(view.id)} aria-label={label}
              className="min-w-0 scroll-mt-28 lg:[grid-column:var(--section-column)] lg:[grid-row:var(--section-row)]"
              style={{
                '--section-column': `${Math.floor(view.placement.position.col / Math.max(1, composition.layout.columns) * 12) + 1} / span ${span}`,
                '--section-row': view.placement.position.row + 1,
              } as CSSProperties}>
              {workSurface && documentOutputs !== undefined
                ? <WorkActionsProvider key={JSON.stringify([workSurface.resultSource?.id, view.id])} {...workSurface} onResultTitleChange={undefined}
                    resultSource={workSurface.resultSource && documentOutputs[view.id]
                      ? { ...workSurface.resultSource, output: documentOutputs[view.id] } : undefined}>
                    {content}
                  </WorkActionsProvider> : content}
            </section>
          );
        })}
      </div>
    </div>
  );
};
